import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import FieldUI from './pages/FieldUI';
import FieldEngineerDashboard from './pages/FieldEngineerDashboard';
import FieldTicketDetail from './pages/FieldTicketDetail';
import Dashboard from './pages/Dashboard';
import TicketDetail from './pages/TicketDetail';
import DropdownSelect from './components/DropdownSelect';
import { startConnectivityMonitor, stopConnectivityMonitor, recheckConnectivity, onSyncEvent } from './utils/offlineCache';

const STATUS_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

const AUTH_STORAGE_KEY = 'fieldassist-auth';
const THEME_STORAGE_KEY = 'fieldassist-theme';
const THEMES = [
  { id: '', label: 'Cream & Mint' },
  { id: 'slate', label: 'Slate' },
  { id: 'red', label: 'Red' },
];

function getNextTheme(currentTheme) {
  const idx = THEMES.findIndex((t) => (t.id || '') === (currentTheme || ''));
  const next = THEMES[(idx + 1) % THEMES.length];
  return next.id;
}

function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [isOnline, setIsOnline] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_STORAGE_KEY) || '');
  const [syncBanner, setSyncBanner] = useState(null);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  useEffect(() => {
    startConnectivityMonitor((status) => {
      setIsOnline(status);
    });

    const unsub = onSyncEvent((event) => {
      if (event.type === 'start') {
        setSyncBanner({ type: 'syncing', message: `Syncing ${event.count} offline ticket${event.count > 1 ? 's' : ''}...` });
      } else if (event.type === 'complete') {
        const msg = event.conflicts && event.conflicts.length > 0
          ? `Synced ${event.synced} ticket${event.synced > 1 ? 's' : ''}. ${event.conflicts.length} need review.`
          : `Synced ${event.synced} ticket${event.synced > 1 ? 's' : ''} successfully.`;
        setSyncBanner({ type: 'success', message: msg });
        setTimeout(() => setSyncBanner(null), 6000);
      } else if (event.type === 'error') {
        setSyncBanner({ type: 'error', message: 'Sync failed — will retry when connection is stable.' });
        setTimeout(() => setSyncBanner(null), 6000);
      }
    });

    return () => {
      stopConnectivityMonitor();
      unsub();
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme) {
      root.setAttribute('data-theme', theme);
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } else {
      root.removeAttribute('data-theme');
      localStorage.removeItem(THEME_STORAGE_KEY);
    }
  }, [theme]);

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const isAdmin = user.role === 'admin';

  return (
    <Router>
      <div className="app-container">
        {/* Offline Banner */}
        {!isOnline && (
          <div className="offline-banner">
            <span className="offline-banner__dot"></span>
            OFFLINE MODE — Actions will sync when connectivity is restored
            <button
              type="button"
              className="offline-banner__retry"
              onClick={() => recheckConnectivity(setIsOnline)}
            >
              Retry connection
            </button>
          </div>
        )}

        {/* Sync Banner */}
        {syncBanner && (
          <div className={`sync-banner sync-banner--${syncBanner.type}`}>
            {syncBanner.type === 'syncing' && <span className="sync-banner__spinner" />}
            {syncBanner.type === 'success' && <span>✓</span>}
            {syncBanner.type === 'error' && <span>✗</span>}
            {syncBanner.message}
            {syncBanner.type !== 'syncing' && (
              <button type="button" className="sync-banner__close" onClick={() => setSyncBanner(null)}>×</button>
            )}
          </div>
        )}

        {/* Top Navigation */}
        <nav className="top-bar">
          <NavLink to="/" className="top-bar__brand">
            <span className="top-bar__brand-icon">R</span>
            Relay
          </NavLink>
          <div className="top-bar__nav">
            {!isAdmin && (
              <>
                <NavLink
                  to="/field"
                  className={({ isActive }) =>
                    `top-bar__link ${isActive ? 'top-bar__link--active' : ''}`
                  }
                >
                  New Ticket
                </NavLink>
                <NavLink
                  to="/field/requests"
                  className={({ isActive }) =>
                    `top-bar__link ${isActive ? 'top-bar__link--active' : ''}`
                  }
                >
                  My Tickets
                </NavLink>
              </>
            )}
            {isAdmin && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `top-bar__link ${isActive ? 'top-bar__link--active' : ''}`
                }
              >
                Dashboard
              </NavLink>
            )}
            <DropdownSelect
              id="connection-status"
              className="top-bar__status-dropdown"
              options={STATUS_OPTIONS}
              value={isOnline ? 'online' : 'offline'}
              onChange={(val) => setIsOnline(val === 'online')}
              placeholder="Status"
            />
            <button
              type="button"
              className="top-bar__theme-btn"
              onClick={() => setTheme((t) => getNextTheme(t))}
              aria-label="Switch theme"
              title="Switch theme"
            >
              <span className="top-bar__theme-btn-icon" aria-hidden>◇</span>
            </button>
            <button
              type="button"
              className="top-bar__user-btn"
              onClick={handleLogout}
              title={`Signed in as ${user.name} — click to sign out`}
            >
              <span className="top-bar__user-avatar">
                {user.name.charAt(0)}
              </span>
              <span className="top-bar__user-name">{user.name.split(' ')[0]}</span>
            </button>
          </div>
        </nav>

        {/* Routes */}
        <Routes>
          {!isAdmin ? (
            <>
              <Route path="/" element={<FieldUI isOnline={isOnline} />} />
              <Route path="/field" element={<FieldUI isOnline={isOnline} />} />
              <Route path="/field/requests" element={<FieldEngineerDashboard isOnline={isOnline} />} />
              <Route path="/field/requests/:ticketId" element={<FieldTicketDetail isOnline={isOnline} />} />
              <Route path="*" element={<Navigate to="/field" replace />} />
            </>
          ) : (
            <>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/ticket/:ticketId" element={<TicketDetail />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </>
          )}
        </Routes>
      </div>
    </Router>
  );
}

export default App;
